import React, { useState, useEffect, useContext, useMemo } from 'react';
import type { CurrencyCode, IKeyholderDataWeb } from '@/app-package';
import { featureFlagClient } from 'utils';
import { SupraController } from 'api';
import { getReactivationPrices } from 'api/SupraController';
import { message } from 'antd';
import { AuthContext } from 'contexts';

type ContextShape = {
  /** The billing profile. */
  billingProfile: IKeyholderDataWeb | undefined;
  /**
   * Updates the billing profile.
   * @param updatedBillingProfile - The updated billing profile.
   */
  updateBillingProfile: (
    updatedBillingProfile: IKeyholderDataWeb,
    resetPaymentStatus?: boolean,
  ) => Promise<void>;
  /** Whether the billing profile is being fetched. */
  isFetchingProfile: boolean;
  /** Whether the invoices are being fetched. */
  isFetchingInvoices: boolean;
  /** Whether the documents are being fetched. */
  isFetchingDocuments: boolean;
  /** Whether the billing profile is being updated. */
  isUpdatingProfile: boolean;
  /** Error thrown when there is a problem fetching the billing profile. */
  fetchProfileError: Error | undefined;
  /** Error thrown when there is a problem updating the billing profile. */
  updateProfileError: Error | undefined;
  /** Whether the fees for a user with an overdue balance are being fetched. */
  loadingOverdueFees: 'loading' | 'done' | 'error';
  /** The prorated price a user must pay to re-activate an overdue subscription. */
  proratedPricing: number | undefined;
  /** The extra fee amount a user has to pay to re-activate their subscription after it has been overdue for too long. */
  reinstatementFee: number | undefined;
  /** The currency code for the billing profile. */
  currencyCode: CurrencyCode | undefined;
  /** Boolean for whether a card is saved and the email and address can be updated. */
  hasSavedCard: boolean;
};

const defaultContext: ContextShape = {
  billingProfile: undefined,
  updateBillingProfile: async () => Promise.resolve(),
  isFetchingProfile: false,
  isUpdatingProfile: false,
  isFetchingInvoices: false,
  isFetchingDocuments: false,
  fetchProfileError: undefined,
  updateProfileError: undefined,
  loadingOverdueFees: 'loading',
  proratedPricing: undefined,
  reinstatementFee: undefined,
  currencyCode: undefined,
  hasSavedCard: false,
};

export const BillingInvoicesPageContext =
  React.createContext<ContextShape>(defaultContext);

/**
 * Indicates (via `response`) whether the user has permission
 * to manage some aspect of the billing profile.
 *
 * If the user does not have permission, the `message` field
 * will contain the reason why.
 */
export type CanManageType =
  | { response: true }
  | { response: false; message: string };

/**
 * Indicates (via `response`) whether the user has an overdue
 * balance that is still unpaid.
 *
 * If the user does is overdue by some amount, the `message` field
 * will display the confirmation for the user to agree to pay the
 * amount.
 */
export type OverdueBalanceType =
  | { response: false }
  | { response: true; message: React.ReactNode };

export interface BillingInvoicesPageContextProps {
  orgId?: string;
  billingCustomerNumber?: string;
}

const hasRequiredFeatureFlags = () => {
  const keyFF = featureFlagClient.featureEnabled('keyholderBillingFeature');
  const supraFF = featureFlagClient.featureEnabled('supraBillingApiFeature');
  return keyFF && supraFF;
};

const BillingInvoicesPageContextProvider: React.FC<
  BillingInvoicesPageContextProps
> = ({ orgId, billingCustomerNumber, children }) => {
  const [isGettingProfile, setIsGettingProfile] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isFetchingInvoices, setIsFetchingInvoices] = useState(false);
  const [isFetchingDocuments, setIsFetchingDocuments] = useState(false);
  const [currencyCode, setCurrencyCode] = useState<CurrencyCode>();
  const [billingProfile, setBillingProfile] = useState<IKeyholderDataWeb>();
  const [getProfileError, setGetProfileError] = useState<Error>();
  const [updateProfileError, setUpdateProfileError] = useState<Error>();
  const [loadingOverdueFees, setLoadingOverdueFees] = useState<
    'loading' | 'done' | 'error'
  >('done');
  const [proratedPricing, setProratedPricing] = useState<number | undefined>(
    undefined,
  );
  const [reinstatementFee, setReinstatementFee] = useState<number | undefined>(
    undefined,
  );

  const {
    auth: user,
    fetchAuth,
    fetchSupraSubscription,
  } = useContext(AuthContext);

  useEffect(() => {
    if (
      hasRequiredFeatureFlags() &&
      user.supraSubscription &&
      user.supraSubscription.status === 'deactivated'
    ) {
      // Grab the prices to display if the user has a Deactivated (unpaid) subscription
      const fetchReactivationPrices = async (): Promise<void> => {
        setLoadingOverdueFees('loading');
        try {
          const {
            reinstatementFee: fetchedReinstatementFee,
            proratedPricing: fetchedProratedPricing,
            currencyCode: fetchedCurrencyCode,
          } = await getReactivationPrices({ userId: user.id });

          // Return everything
          setReinstatementFee(fetchedReinstatementFee);
          setProratedPricing(fetchedProratedPricing);
          setCurrencyCode(fetchedCurrencyCode);
          setLoadingOverdueFees('done');
        } catch {
          // Let the user know if anything goes wrong
          message.error(
            `Encountered error while fetching the data for your prorated pricing. Please contact the Supra customer success team.`,
          );
          setLoadingOverdueFees('error');
        }
      };
      fetchReactivationPrices();
    }
  }, [user.id, user.supraSubscription]);

  const hasSavedCard = useMemo(
    () => Boolean(billingProfile?.CCNumber && billingProfile?.CardholderName),
    [billingProfile?.CCNumber, billingProfile?.CardholderName],
  );

  const updateBillingProfile = async (
    updatedBillingProfile: IKeyholderDataWeb,
    resetPaymentStatus?: boolean,
  ) => {
    if (!hasRequiredFeatureFlags()) {
      setUpdateProfileError(new Error('Required feature flags not enabled'));
      return;
    }

    if (!updatedBillingProfile) {
      setUpdateProfileError(new Error('No billing profile provided'));
      return;
    }

    setIsUpdatingProfile(true);
    setIsFetchingInvoices(true);
    setIsFetchingDocuments(true);
    const profile = await SupraController.updateKeyholderBillingProfile({
      billingProfile: updatedBillingProfile,
      resetPaymentStatus: resetPaymentStatus ?? false,
    }).catch(error => {
      // Set error state based on the error returned from the API
      if ('OperationMessage' in error) {
        setUpdateProfileError(new Error(error.OperationMessage));
      } else if (error instanceof Error) {
        setUpdateProfileError(error);
      } else {
        setUpdateProfileError(
          new Error(
            'Unable to update billing profile. Please try again later.',
          ),
        );
      }

      // Ensure loading state is set to false
      setIsUpdatingProfile(false);

      setIsFetchingInvoices(false);
      setIsFetchingDocuments(false);

      // Re-throw the error to be caught by the caller
      throw error;
    });

    if (resetPaymentStatus) {
      // Update user and subscription state if payment status is reset
      await fetchAuth();
      await fetchSupraSubscription();
    }
    setBillingProfile(profile);
    setUpdateProfileError(undefined);
    setIsUpdatingProfile(false);
    setIsFetchingInvoices(false);
    setIsFetchingDocuments(false);
  };

  useEffect(() => {
    if (!hasRequiredFeatureFlags()) {
      // setGetProfileError(new Error('Required feature flags not enabled'));
      return;
    }

    if (billingCustomerNumber === undefined) {
      // setGetProfileError(new Error('No billing customer number provided'));
      return;
    }

    if (orgId === undefined) {
      // setGetProfileError(new Error('No org id provided'));
      return;
    }

    setIsGettingProfile(true);
    SupraController.getKeyholderBillingProfile({ orgId, billingCustomerNumber })
      .then(profile => {
        if (profile === undefined) {
          throw new Error('No billing profile found');
        }
        setBillingProfile(profile);
        setGetProfileError(undefined);
      })
      .catch(() =>
        setGetProfileError(new Error('Billing profile cannot be retrieved')),
      )
      .finally(() => {
        setIsGettingProfile(false);
      });
  }, [billingCustomerNumber, orgId]);

  return (
    <BillingInvoicesPageContext.Provider
      value={{
        billingProfile,
        updateBillingProfile,
        isFetchingProfile: isGettingProfile,
        isUpdatingProfile,
        isFetchingInvoices,
        isFetchingDocuments,
        fetchProfileError: getProfileError,
        updateProfileError,
        loadingOverdueFees,
        proratedPricing,
        currencyCode,
        reinstatementFee,
        hasSavedCard,
      }}
    >
      {children}
    </BillingInvoicesPageContext.Provider>
  );
};

export default BillingInvoicesPageContextProvider;
